import React, {useState} from 'react';
import {Drawer, Typography, Avatar, Button, List, Tooltip, Popconfirm, Mentions} from 'antd';
import {MentionsInput, Mention} from 'react-mentions';
import {MessageOutlined, DeleteOutlined, EditOutlined, SendOutlined} from '@ant-design/icons';
import {BoardService} from './boardService';

const {Title, Paragraph} = Typography;

const renderTextWithLinksAndMentions = (text = '', users = []) => {
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;
    const mentionRegex = /@(\d+)/g;

    if (!text) return text;

    // Replace mentions with formatted HTML
    const textWithMentions = text.replace(mentionRegex, (match, userId) => {
        const user = users.find((u) => `${u.id}` === `${userId}`);
        return `<span class="text-blue-600">@${user ? user.name : userId}</span>`;
    });

    // Replace URLs with formatted HTML
    const processedText = textWithMentions.replace(urlRegex, (match) => {
        const href = match.startsWith('www.') ? `https://${match}` : match;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="underline">${match}</a>`;
    });

    return processedText;
};

const CommentInput = ({value, onChange, onSubmit, users, placeholder, handleCancel}) => {
    const mentionInputStyle = {
        control: {
            minHeight: '80px',
            padding: '8px 12px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            backgroundColor: 'white',
            fontSize: '14px',
            fontFamily: 'inherit'
        },
        input: {
            margin: 0,
            padding: 0,
            outline: 'none'
        },
        suggestions: {
            list: {
                backgroundColor: 'white',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                fontSize: '14px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                maxHeight: '300px',
                overflowY: 'auto',
                position: 'fixed',  // Change to fixed positioning
                zIndex: 1050       // Higher z-index to ensure visibility
            },
            item: {
                padding: '8px 12px',
                '&focused': {
                    backgroundColor: '#e6f7ff'
                }
            }
        }
    };

    const renderSuggestion = (suggestion, search, highlightedDisplay, index, focused) => {
        return (
            <div
                className={`flex items-center gap-2 p-2 ${focused ? 'bg-blue-50' : 'hover:bg-gray-50'} cursor-pointer`}>
                <Avatar
                    size="small"
                    src={`/${suggestion.profile_image}`}
                    className={`${!suggestion.profile_image ? 'bg-blue-500' : ''}`}
                >
                    {!suggestion.profile_image && suggestion.display.charAt(0)}
                </Avatar>
                <span>{suggestion.display}</span>
            </div>
        );
    };

    return (
        <div className="relative">
            <MentionsInput
                value={value}
                onChange={(e, newValue) => onChange(newValue)}
                style={mentionInputStyle}
                placeholder={placeholder}
                className="mentions-input"
                a11ySuggestionsListLabel="Suggested mentions"
                forceSuggestionsAboveCursor
            >
                <Mention
                    trigger="@"
                    data={users.map(user => ({
                        id: user.id,
                        display: user.name,
                        profile_image: user.profile_image
                    }))}
                    renderSuggestion={renderSuggestion}
                    displayTransform={(id, display) => `@${display}`}
                    markup="@[__display__](__id__)"
                    appendSpaceOnAdd
                />
            </MentionsInput>

            <div className={"flex gap-4 justify-end py-2"}>
                <Button
                    type="primary"
                    size={"small"}
                    onClick={onSubmit}
                    className="rounded-md shadow-2xl"
                >
                    Comment
                </Button>

                {handleCancel ? <Button
                    size={"small"}
                    onClick={handleCancel}
                    className="rounded-md shadow-2xl"
                >
                    Cancel
                </Button> : null}
            </div>

        </div>
    );
};

const MentionComment = ({value, onChange, users, placeholder}) => (
    <Mentions
        value={value}
        onChange={onChange}
        options={users.map(user => ({
            value: `(**#${user.id}${user.name}**`,
            label: (
                <div className="flex items-center gap-2">
                    <Avatar
                        size="small"
                        src={`/${user.profile_image}`}
                        className={`${!user.profile_image ? 'bg-blue-500' : ''}`}
                    >
                        {!user.profile_image && user.name.charAt(0)}
                    </Avatar>
                    <span>{user.name}</span>
                </div>
            ),
        }))}
        placeholder={placeholder}
        autoSize={{minRows: 2, maxRows: 6}}
        className="mb-2"
        placement="top"
    />
);

const Comment = ({comment, onEdit, onDelete, currentUserId, users}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedContent, setEditedContent] = useState('');

    const handleStartEditing = () => {
        // Convert the ID mentions to react-mentions format when starting edit
        const mentionRegex = /@(\d+)/g;
        const contentWithMentions = comment.content.replace(mentionRegex, (match, userId) => {
            const user = users.find(u => u.id.toString() === userId);
            return user ? `@[${user.name}](${userId})` : match;
        });

        setEditedContent(contentWithMentions);
        setIsEditing(true);
    };

    const handleSave = async () => {
        // Convert mentions format to backend format before saving
        const mentionRegex = /@\[(.*?)\]\((\d+)\)/g;
        const contentWithIds = editedContent.replace(mentionRegex, '@$2');

        await onEdit(comment.id, contentWithIds);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedContent('');
    };

    const timeDiff = new Date() - new Date(comment.created_at);
    const minutesAgo = Math.floor(timeDiff / (1000 * 60));
    const hoursAgo = Math.floor(minutesAgo / 60);
    const daysAgo = Math.floor(hoursAgo / 24);

    let timeAgo;
    if (daysAgo > 0) {
        timeAgo = `${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`;
    } else if (hoursAgo > 0) {
        timeAgo = `${hoursAgo} ${hoursAgo === 1 ? 'hour' : 'hours'} ago`;
    } else if (minutesAgo > 0) {
        timeAgo = `${minutesAgo} ${minutesAgo === 1 ? 'minute' : 'minutes'} ago`;
    } else {
        timeAgo = 'just now';
    }

    const isOwnComment = comment.user.id === currentUserId;

    return (
        <div className="flex gap-3 py-3">
            <Avatar
                src={`/${comment.user.profile_image}`}
                className={`${!comment.user.profile_image ? 'bg-blue-500' : ''}`}
            >
                {!comment.user.profile_image && comment.user.name.charAt(0)}
            </Avatar>
            <div className="flex-1">
                <div className="flex items-center gap-2">
                    <span className="font-medium">{comment.user.name}</span>
                    <span className="text-gray-400 text-sm">{timeAgo}</span>
                </div>
                {isEditing ? (
                    <div className="mt-2">
                        <CommentInput
                            value={editedContent}
                            onChange={setEditedContent}
                            onSubmit={handleSave}
                            users={users}
                            placeholder="Edit comment..."
                            handleCancel={handleCancel}
                        />
                        {/*<div className="flex gap-2 mt-2">
                            <Button size="small" onClick={handleCancel}>
                                Cancel
                            </Button>
                        </div>*/}
                    </div>
                ) : (
                    <p className="mt-1 mb-0 text-gray-600">
                        <p dangerouslySetInnerHTML={{__html: renderTextWithLinksAndMentions(comment.content, users)}}/>
                    </p>
                )}
            </div>
            {isOwnComment && !isEditing && (
                <div className="flex gap-2">
                    <Button
                        type="text"
                        icon={<EditOutlined/>}
                        onClick={handleStartEditing}
                        size="small"
                    />
                    <Popconfirm
                        title="Delete comment?"
                        description="Are you sure you want to delete this comment?"
                        onConfirm={() => onDelete(comment.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="text"
                            icon={<DeleteOutlined/>}
                            danger
                            size="small"
                        />
                    </Popconfirm>
                </div>
            )}
        </div>
    );
};

export const TaskDetailsDrawer = ({task, isOpen, onClose, currentUser, users = []}) => {
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    React.useEffect(() => {
        const fetchComments = async () => {
            if (task?.id && isOpen) {
                setLoading(true);
                try {
                    const response = await BoardService.getComments(task.board_id, task.id);
                    setComments(response);
                } catch (error) {
                    console.error('Failed to fetch comments:', error);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchComments();
    }, [task?.id, isOpen, task?.board_id]);

    const handleAddComment = async () => {
        if (!newComment.trim()) return;

        setSubmitting(true);
        try {
            // Convert the mentions format to backend format
            const mentionRegex = /@\[(.*?)\]\((\d+)\)/g;
            const commentWithIds = newComment.replace(mentionRegex, '@$2');

            const response = await BoardService.createComment(task.board_id, task.id, {
                content: commentWithIds
            });
            setComments([response, ...comments]);
            setNewComment('');
        } catch (error) {
            console.error('Failed to add comment:', error);
        } finally {
            setSubmitting(false);
        }
    };

    // Render function to convert backend format to display format
    const renderTextWithMentions = (text) => {
        const idMentionRegex = /@(\d+)/g;
        return text.replace(idMentionRegex, (match, userId) => {
            const user = users.find(u => u.id.toString() === userId);
            return user ? `@${user.name}` : match;
        });
    };

    const handleEditComment = async (commentId, content) => {
        try {
            const response = await BoardService.updateComment(task.board_id, task.id, commentId, {
                content
            });
            setComments(comments.map(comment =>
                comment.id === commentId ? response : comment
            ));
        } catch (error) {
            console.error('Failed to edit comment:', error);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await BoardService.deleteComment(task.board_id, task.id, commentId);
            setComments(comments.filter(comment => comment.id !== commentId));
        } catch (error) {
            console.error('Failed to delete comment:', error);
        }
    };

    if (!task) return null;

    return (
        <Drawer
            title={null}
            placement="right"
            onClose={onClose}
            open={isOpen}
            width={640}
            className="task-details-drawer"
            styles={{
                body: {
                    padding: 0,
                }
            }}
        >
            <div className="px-6 py-4">
                <div className="space-y-4">
                    {/* Task Header */}
                    <div className="flex items-start justify-between">
                        <Title level={4} className="m-0">{task.title}</Title>
                        {task.assignee && (
                            <Tooltip title={task.assignee.name}>
                                <Avatar
                                    src={`/${task.assignee.profile_image}`}
                                    className={`${!task.assignee.profile_image ? 'bg-blue-500' : ''}`}
                                >
                                    {!task.assignee.profile_image && task.assignee.name.charAt(0)}
                                </Avatar>
                            </Tooltip>
                        )}
                    </div>

                    {/* Task Description */}
                    <div className="rounded-lg p-4">
                        <Paragraph className="whitespace-pre-wrap mb-0">
                            <div dangerouslySetInnerHTML={{__html: renderTextWithLinksAndMentions(task.description)}}/>
                        </Paragraph>
                    </div>

                    {/* Comments Section */}
                    <div>
                        <div className="flex items-center gap-2 text-gray-600 mb-4">
                            <MessageOutlined/>
                            <span className="font-medium">Comments</span>
                        </div>

                        {/* Add Comment */}
                        <div className="mb-4">
                            <div className="flex gap-3">
                                <Avatar
                                    src={`/${currentUser.profile_image}`}
                                    className={`${!currentUser.profile_image ? 'bg-blue-500' : ''}`}
                                >
                                    {!currentUser.profile_image && currentUser.name.charAt(0)}
                                </Avatar>
                                <div className="flex-1">
                                    <CommentInput
                                        value={newComment}
                                        onChange={setNewComment}
                                        onSubmit={handleAddComment}
                                        users={users}
                                        placeholder="Add a comment... (use @ to mention users)"
                                        loading={submitting}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Comments List */}
                        <List
                            loading={loading}
                            dataSource={comments}
                            renderItem={comment => (
                                <Comment
                                    key={comment.id}
                                    comment={comment}
                                    onEdit={handleEditComment}
                                    onDelete={handleDeleteComment}
                                    currentUserId={currentUser.id}
                                    users={users}
                                />
                            )}
                            locale={{
                                emptyText: 'No comments yet'
                            }}
                        />
                    </div>
                </div>
            </div>
        </Drawer>
    );
};
